import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { SharedModule } from '../../shared/shared.module';

import {
  NavigationItem,
  navigationItemsInvoices,
} from '../../models/navigation-item-model';
import { LabelService } from '../../services/label.service';
import { classes, pages } from '../../shared/labels';
import { InvoicesService } from '../../services/invoices.service';
import { TableInvoiceFields } from '../../models/invoices';
import Chart from 'chart.js/auto';
import { ErrorPanelService } from '../../services/error-panel.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-invoices-sales',
  standalone: true,
  imports: [SharedModule, FormsModule],
  templateUrl: './invoices-sales.component.html',
  styleUrl: './invoices-sales.component.scss',
})
export class InvoicesSalesComponent implements OnInit {
  @ViewChild('pieChart') pieChartCanvas!: ElementRef;
  private chart: Chart<'pie', number[], string> | undefined;

  navigationItems: NavigationItem[];
  totalInvoices: number = 0;
  totalSDIInvoices: number = 0;
  totalSTSInvoices: number = 0;

  invoicesSDI: TableInvoiceFields[] = [];
  invoicesSTS: TableInvoiceFields[] = [];

  isLoading: boolean = true;
  isLoadingSDI: boolean = true;
  isLoadingSTS: boolean = true;

  availableYears: string[] = [];
  selectedYear: string = new Date().getFullYear().toString();

  constructor(
    public labelService: LabelService,
    private invoicesService: InvoicesService,
    private errorPanelService: ErrorPanelService,
    private cdr: ChangeDetectorRef
  ) {
    this.navigationItems = navigationItemsInvoices.map((item) => {
      if (item.label === pages.invoices_sales) {
        return { ...item, class: classes.hnb };
      }
      return { ...item };
    });
  }

  ngOnInit(): void {
    this.validateDoctorBillingData();
  }

  validateDoctorBillingData() {
    this.invoicesService.validateDoctorBillingData().subscribe({
      next: (res) => {
        if (res.sts_valid) {
          this.getInvoicesSTS();
        }
        if (res.sdi_valid) {
          this.getInvoicesSDI();
        }
        if (!res.sts_valid && !res.sdi_valid) {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error(
          'Errore nella validazione dei dati di fatturazione.',
          err
        );
        this.errorPanelService.showErrorSnackBar(
          'Errore nella validazione dei dati di fatturazione.'
        );
      },
    });
  }

  private initChart(): void {
    if (!this.pieChartCanvas?.nativeElement) {
      return;
    }

    const ctx = this.pieChartCanvas.nativeElement.getContext('2d');

    // Destroy existing chart if it exists
    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['SDI', 'STS'],
        datasets: [
          {
            data: [this.totalSDIInvoices, this.totalSTSInvoices],
            backgroundColor: [
              'rgba(54, 162, 235, 0.8)',
              'rgba(255, 99, 132, 0.8)',
            ],
            borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
        },
      },
    });
  }

  getInvoices(): void {
    this.getInvoicesSDI();
    this.getInvoicesSTS();
  }

  getInvoicesSDI(): void {
    this.invoicesService.getSDIInvoices().subscribe({
      next: (invoices) => {
        this.invoicesSDI = invoices;
        this.isLoadingSDI = false;
        this.isLoading = this.isLoadingSDI || this.isLoadingSTS;
        this.updateAvailableYears();
        this.calculateTotals();
      },
      error: (error) => {
        console.error('Errore nel caricare le fatture SDI.', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel caricare le fatture SDI.'
        );
        this.isLoadingSDI = false;
        this.isLoading = this.isLoadingSDI || this.isLoadingSTS;
      },
    });
  }

  getInvoicesSTS(): void {
    this.invoicesService.getSTSInvoices().subscribe({
      next: (invoices) => {
        this.invoicesSTS = invoices;
        this.isLoadingSTS = false;
        this.isLoading = this.isLoadingSDI || this.isLoadingSTS;
        this.updateAvailableYears();
        this.calculateTotals();
      },
      error: (error) => {
        console.error('Errore nel caricare le fatture STS.', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel caricare le fatture STS.'
        );
        this.isLoadingSTS = false;
        this.isLoading = this.isLoadingSDI || this.isLoadingSTS;
      },
    });
  }

  private updateAvailableYears(): void {
    const years = new Set<string>();
    this.invoicesSDI.forEach((invoice) =>
      years.add(invoice.progressivo_anno.toString())
    );
    this.invoicesSTS.forEach((invoice) =>
      years.add(invoice.progressivo_anno.toString())
    );
    this.availableYears = Array.from(years).sort().reverse();

    // If current selected year is not in the list, select the most recent year
    if (
      !this.availableYears.includes(this.selectedYear) &&
      this.availableYears.length > 0
    ) {
      this.selectedYear = this.availableYears[0];
    }
  }

  onYearChange(year: string): void {
    this.selectedYear = year;
    this.calculateTotals();
  }

  calculateTotals(): void {
    // Reset totals
    this.totalInvoices = 0;
    this.totalSDIInvoices = 0;
    this.totalSTSInvoices = 0;

    // Filter and calculate SDI invoices
    this.invoicesSDI
      .filter(
        (invoice) =>
          invoice.progressivo_anno.toString() === this.selectedYear.toString()
      )
      .forEach((invoice) => {
        if (!invoice.tipo.includes('Nota di Credito')) {
          this.totalSDIInvoices += parseFloat(invoice.totale);
        } else {
          this.totalSDIInvoices -= parseFloat(invoice.totale);
        }
      });

    // Filter and calculate STS invoices
    this.invoicesSTS
      .filter(
        (invoice) =>
          invoice.progressivo_anno.toString() === this.selectedYear.toString()
      )
      .forEach((invoice) => {
        if (!invoice.tipo.includes('Nota di Credito')) {
          this.totalSTSInvoices += parseFloat(invoice.totale);
        } else {
          this.totalSTSInvoices -= parseFloat(invoice.totale);
        }
      });

    // Round to 2 decimal places
    this.totalSDIInvoices = Number(this.totalSDIInvoices.toFixed(2));
    this.totalSTSInvoices = Number(this.totalSTSInvoices.toFixed(2));
    this.totalInvoices = Number(
      (this.totalSDIInvoices + this.totalSTSInvoices).toFixed(2)
    );

    // Force change detection
    this.cdr.detectChanges();

    // Update or create chart
    setTimeout(() => {
      if (this.chart) {
        // Update existing chart
        this.chart.data.datasets[0].data = [
          this.totalSDIInvoices,
          this.totalSTSInvoices,
        ];
        this.chart.update('none'); // Add animation mode
      } else if (this.pieChartCanvas?.nativeElement) {
        // Create new chart
        this.initChart();
      }
    });
  }
}
